<template>
  <div class="field-designer">
    <a-table
      :columns="columns"
      :data-source="modelValue"
      :pagination="false"
      :scroll="{ x: 'max-content' }"
      :rowKey="rowKeyFn"
      size="small"
      bordered
    >
      <template #bodyCell="{ column, record, index }">
        <template v-if="column.key === 'label'">
          <a-input
            v-model:value="record.label"
            :disabled="disabled"
            placeholder="如：手机号"
            :maxlength="20"
            @change="onTouched"
          />
        </template>
        <template v-else-if="column.key === 'key'">
          <a-input
            v-model:value="record.key"
            :disabled="disabled || record.system"
            placeholder="如：phone (英文)"
            :maxlength="30"
            @change="onTouched"
          />
        </template>
        <template v-else-if="column.key === 'type'">
          <a-select
            v-model:value="record.type"
            :disabled="disabled || record.system"
            :options="typeOptions"
            style="width: 100%"
            @change="onTouched"
          />
        </template>
        <template v-else-if="column.key === 'required'">
          <a-checkbox v-model:checked="record.required" :disabled="disabled" @change="onTouched" />
        </template>
        <template v-else-if="column.key === 'validate'">
          <a-select
            v-model:value="record.validate"
            :disabled="disabled || record.system"
            :options="validateOptions"
            allow-clear
            placeholder="无"
            style="width: 100%"
            @change="onTouched"
          />
        </template>
        <template v-else-if="column.key === 'options'">
          <a-button
            type="link"
            size="small"
            :disabled="disabled || record.type !== 'select'"
            @click="onEditOptions(record, index)"
          >
            {{ record.options?.length || 0 }} 项
          </a-button>
        </template>
        <template v-else-if="column.key === 'action'">
          <a-space>
            <a-button
              type="link"
              size="small"
              :disabled="disabled || index === 0"
              @click="onMove(index, -1)"
            >
              上移
            </a-button>
            <a-button
              type="link"
              size="small"
              :disabled="disabled || index === modelValue.length - 1"
              @click="onMove(index, 1)"
            >
              下移
            </a-button>
            <a-button
              type="link"
              size="small"
              danger
              :disabled="disabled || record.system"
              @click="onRemove(index)"
            >
              删除
            </a-button>
          </a-space>
        </template>
      </template>
    </a-table>

    <div class="footer">
      <a-button type="dashed" :disabled="disabled || modelValue.length >= maxFields" @click="onAdd">
        + 添加字段
      </a-button>
      <span class="hint">最多 {{ maxFields }} 个字段；标记为系统字段（姓名、身份证）不可删除</span>
    </div>

    <!-- 选项编辑弹窗（type=select 时使用） -->
    <a-modal
      v-model:open="optionsModal.open"
      :title="`编辑选项 - ${optionsModal.label}`"
      @ok="onConfirmOptions"
      okText="确定"
      cancelText="取消"
    >
      <div v-for="(o, oi) in optionsModal.options" :key="oi" class="option-row">
        <a-input v-model:value="o.label" placeholder="显示文本" style="width: 45%" />
        <a-input v-model:value="o.value" placeholder="提交值" style="width: 45%; margin-left: 8px" />
        <a-button type="link" danger size="small" @click="optionsModal.options.splice(oi, 1)">
          删除
        </a-button>
      </div>
      <a-button
        type="dashed"
        block
        @click="optionsModal.options.push({ label: '', value: '' })"
        style="margin-top: 8px"
      >
        + 添加选项
      </a-button>
    </a-modal>
  </div>
</template>

<script setup>
import { reactive } from 'vue';

const props = defineProps({
  // 受控值，结构为字段定义数组（必须是父组件 reactive 的同一引用）
  modelValue: { type: Array, default: () => [] },
  disabled: { type: Boolean, default: false },
  maxFields: { type: Number, default: 8 },
});
const emits = defineEmits(['update:modelValue', 'change']);

const typeOptions = [
  { label: '文本', value: 'input' },
  { label: '数字', value: 'inputNumber' },
  { label: '下拉', value: 'select' },
  { label: '多行文本', value: 'textarea' },
];

const validateOptions = [
  { label: '姓名', value: 'name' },
  { label: '手机号', value: 'mobile' },
  { label: '身份证', value: 'idCard' },
  { label: '邮箱', value: 'email' },
];

// 表格 rowKey：使用稳定的引用 + 索引拼接，避免 spread 重建破坏行身份
const rowKeyFn = (record) => {
  const idx = props.modelValue.indexOf(record);
  return record.system ? `sys-${record.key}` : `row-${idx}`;
};

// 行内输入直接通过 v-model 改 record（数组共享引用），无需重建数组；
// 这里只用于在 add/remove/move 等结构变化时通知父组件做相同 emit。
const onTouched = () => {
  emits('change', props.modelValue);
};

const onAdd = () => {
  // 给新行一个不冲突的占位 key，提示用户填写真实值
  const used = new Set(props.modelValue.map((i) => i.key));
  let n = props.modelValue.length + 1;
  let placeholder = `field${n}`;
  while (used.has(placeholder)) {
    n += 1;
    placeholder = `field${n}`;
  }
  props.modelValue.push({
    key: placeholder,
    label: '',
    type: 'input',
    required: false,
    validate: undefined,
    options: [],
  });
  emits('change', props.modelValue);
};

const onRemove = (index) => {
  props.modelValue.splice(index, 1);
  emits('change', props.modelValue);
};

const onMove = (index, offset) => {
  const target = index + offset;
  if (target < 0 || target >= props.modelValue.length) return;
  const arr = props.modelValue;
  [arr[index], arr[target]] = [arr[target], arr[index]];
  emits('change', props.modelValue);
};

// 选项编辑
const optionsModal = reactive({
  open: false,
  label: '',
  index: -1,
  options: [],
});
const onEditOptions = (record, index) => {
  optionsModal.label = record.label || '';
  optionsModal.index = index;
  optionsModal.options = (record.options || []).map((o) => ({ ...o }));
  optionsModal.open = true;
};
const onConfirmOptions = () => {
  const target = props.modelValue[optionsModal.index];
  if (target) {
    target.options = optionsModal.options.filter(
      (o) => o.label && String(o.value).length > 0,
    );
  }
  optionsModal.open = false;
  emits('change', props.modelValue);
};

const columns = [
  { title: '字段名(中文)', key: 'label', dataIndex: 'label', width: 130, ellipsis: true },
  { title: '字段Key(英文)', key: 'key', dataIndex: 'key', width: 130, ellipsis: true },
  { title: '类型', key: 'type', dataIndex: 'type', width: 100 },
  { title: '必填', key: 'required', dataIndex: 'required', width: 60, align: 'center' },
  { title: '校验', key: 'validate', dataIndex: 'validate', width: 110 },
  { title: '选项', key: 'options', dataIndex: 'options', width: 70, align: 'center' },
  { title: '操作', key: 'action', width: 180, align: 'center' },
];
</script>

<style lang="less" scoped>
.field-designer {
  width: 100%;

  // 表头不允许像截图那样竖向折叠为单字一列
  :deep(.ant-table-thead > tr > th) {
    white-space: nowrap;
  }
  :deep(.ant-table-tbody > tr > td) {
    padding: 6px 8px;
  }
  // 操作列 a-button 之间紧凑些
  :deep(.ant-btn-link) {
    padding-inline: 4px;
  }

  .footer {
    margin-top: 8px;
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;

    .hint {
      color: #999;
      font-size: 12px;
    }
  }

  .option-row {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
  }
}
</style>
